"use client";

import React from "react";
import {
  Rocket,
  Code,
  Briefcase,
  Users,
  DollarSign,
  Globe,
  Award,
  Zap,
  Target,
  Lightbulb,
} from "lucide-react";
import Image from "next/image";

const benefits = [
  { icon: <Code className="w-5 h-5" />, text: "Showcase your projects, skills, and certifications" },
  { icon: <Briefcase className="w-5 h-5" />, text: "Connect with internships and job opportunities" },
  { icon: <Users className="w-5 h-5" />, text: "Get mentored by industry professionals and alumni" },
  { icon: <DollarSign className="w-5 h-5" />, text: "Access scholarships, grants, and project funding" },
  { icon: <Globe className="w-5 h-5" />, text: "Gain real-world exposure through events and hackathons" },
  { icon: <Award className="w-5 h-5" />, text: "Build your professional network within CONCES community" },
];

const TalentHubComingSoon = () => {
  return (
    <section className="relative overflow-hidden py-16 lg:py-24">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-primary-dark to-slate-900" />
      
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-secondary/20 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 -right-20 w-96 h-96 bg-primary-light/20 rounded-full blur-[120px]" />
        <div className="absolute -bottom-40 left-1/3 w-72 h-72 bg-secondary/15 rounded-full blur-[80px]" />
        
        {/* Dot Pattern */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Section - Visual */}
          <div className="relative order-2 lg:order-1">
            {/* Main Image Container */}
            <div className="relative">
              {/* Glow Effect Behind Image */}
              <div className="absolute inset-0 bg-gradient-to-tr from-secondary/30 via-primary-light/20 to-transparent rounded-3xl blur-2xl transform scale-95" />
              
              {/* Image Card */}
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-yellow-500 flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-primary-dark" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Talent Hub</p>
                    <p className="text-white/50 text-xs">by CONCES</p>
                  </div>
                </div>

                {/* Mock Image/Illustration */}
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-primary-light/20 to-primary-dark/40 mb-6">
                  <Image
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000&auto=format&fit=crop"
                    alt="Students collaborating"
                    fill
                    className="object-cover opacity-80"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-transparent to-transparent" />
                  
                  {/* Floating Elements on Image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 w-fit">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-white text-sm font-medium">Building something amazing...</span>
                    </div>
                  </div>
                </div>

                {/* Mini Feature Cards */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: <Target className="w-4 h-4" />, label: "Opportunities" },
                    { icon: <Lightbulb className="w-4 h-4" />, label: "Mentorship" },
                    { icon: <Zap className="w-4 h-4" />, label: "Growth" },
                  ].map((item, i) => (
                    <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-300">
                      <div className="text-secondary">{item.icon}</div>
                      <span className="text-white/70 text-xs">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Decorative Floating Elements */}
              <div className="absolute -top-4 -right-4 p-3 bg-secondary rounded-2xl shadow-lg shadow-secondary/30 animate-bounce" style={{ animationDuration: '3s' }}>
                <Zap className="w-5 h-5 text-primary-dark" />
              </div>
              <div className="absolute -bottom-4 -left-4 p-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                <Code className="w-5 h-5 text-secondary" />
              </div>
            </div>
          </div>

          {/* Right Section - Content */}
          <div className="order-1 lg:order-2">
            {/* Coming Soon - Humanized */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary"></span>
                </span>
                <span className="text-secondary font-medium">Coming Soon</span>
              </div>
              <span className="text-white/30">—</span>
              <span className="text-white/60 text-sm">We&apos;re working hard to bring this to you</span>
            </div>

            {/* Heading */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Meet{" "}
              <span className="relative">
                <span className="bg-gradient-to-r from-secondary via-yellow-400 to-secondary bg-clip-text text-transparent">
                  CONCES Talent Hub
                </span>
                <svg className="absolute -bottom-1 left-0 w-full h-2" viewBox="0 0 200 8" preserveAspectRatio="none">
                  <path d="M0 7 Q 50 0, 100 7 T 200 7" stroke="url(#gradient)" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.5"/>
                      <stop offset="50%" stopColor="#FBBF24" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.5"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>

            {/* Description */}
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Your launchpad to success. A platform designed specifically for engineering and tech students to showcase talent, find opportunities, and build meaningful connections that accelerate your career.
            </p>

            {/* Benefits */}
            <div className="space-y-4">
              <p className="text-white/50 text-sm uppercase tracking-wider font-medium mb-4">
                What you&apos;ll get
              </p>
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 group"
                >
                  <div className="flex-shrink-0 p-2 rounded-lg bg-white/5 text-secondary group-hover:bg-secondary group-hover:text-primary-dark transition-all duration-300 border border-white/10 group-hover:border-secondary">
                    {benefit.icon}
                  </div>
                  <p className="text-white/80 text-base leading-relaxed pt-0.5 group-hover:text-white transition-colors duration-300">
                    {benefit.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Bottom Note */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-white/50 text-sm flex items-center gap-2">
                <span className="text-secondary">✨</span>
                Stay tuned — something amazing is on the way for CONCES members
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TalentHubComingSoon;
